import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Calculator, ArrowLeft } from "lucide-react";
import Money from "@/components/Money";
import { computePayslip } from "@/lib/tax";

const FIELD = "w-full rounded-md border border-input bg-white px-3 py-2.5 text-base outline-none transition-colors focus:border-primary";
const CYCLE_LABEL = { weekly: "38 hr week", fortnightly: "76 hr fortnight", monthly: "164 hr month" };

export default function PayslipEstimator() {
    const [rate, setRate] = useState(100);
    const [basis, setBasis] = useState("hour");
    const [cycle, setCycle] = useState("weekly");
    const [view, setView] = useState("form");
    const cardRef = useRef(null);
    const [panelHeight, setPanelHeight] = useState(null);

    // Lock the card to the height of page 1 so page 2 never pushes the next section down.
    useLayoutEffect(() => {
        if (view === "form" && cardRef.current) setPanelHeight(cardRef.current.offsetHeight);
    }, [view]);

    const r = useMemo(() => computePayslip({ rate: Number(rate) || 0, basis, cycle, sacrifice: 0 }), [rate, basis, cycle]);

    const rows = [
        [`Contracted rate per ${CYCLE_LABEL[cycle]}`, r.gross, "result-contracted-rate"],
        ["PayMe Fee", -r.fee, "result-payme-fee"],
        ["New Contracted Rate", r.ncr, "result-new-rate"],
        ["Superannuation", -r.super, "result-super"],
        ["Taxable Salary", r.taxable, "result-taxable"],
        ["PAYG Tax", -r.tax, "result-payg"],
    ];

    return (
        <div id="estimator" ref={cardRef} style={view === "results" && panelHeight ? { height: panelHeight } : undefined} className="flex flex-col rounded-xl border border-border bg-white p-6 shadow-[0_10px_40px_-20px_rgba(31,40,56,0.35)] md:p-7" data-testid="payslip-estimator">
            <h2 className="flex items-center gap-2.5 font-display text-2xl font-bold text-primary sm:text-3xl">
                <Calculator size={26} /> Contractor Payroll Calculator
            </h2>

            {view === "form" ? (
                <div data-testid="estimator-form">
                    <p className="mt-2 text-sm text-muted-foreground">Estimate your pay by using this calculator and understanding how contractor superannuation is calculated.</p>

                    <label className="mt-6 block text-base font-semibold" htmlFor="rate-input">My contracted rate</label>
                    <div className="mt-2 grid grid-cols-5 gap-3">
                        <input id="rate-input" data-testid="estimator-rate-input" type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className={`${FIELD} col-span-3`} aria-label="My contracted rate (AUD, excluding GST)" />
                        <select value={basis} onChange={(e) => setBasis(e.target.value)} className={`${FIELD} col-span-2`} data-testid="estimator-basis" aria-label="Contracted rate basis">
                            <option value="hour">Per hour</option>
                            <option value="day">Per day</option>
                            <option value="month">Per month</option>
                        </select>
                    </div>

                    <label className="mt-5 block text-base font-semibold" htmlFor="cycle-select">My contracted pay cycle</label>
                    <select id="cycle-select" value={cycle} onChange={(e) => setCycle(e.target.value)} className={`${FIELD} mt-2`} data-testid="estimator-cycle">
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                    </select>

                    <button onClick={() => setView("results")} data-testid="estimator-calculate-btn" className="mt-5 w-full rounded-md bg-primary py-3 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
                        Calculate
                    </button>
                </div>
            ) : (
                <div data-testid="estimator-results" className="flex flex-1 flex-col">
                    <p className="mt-2 text-xs text-muted-foreground">Indicative only</p>
                    <ul className="mt-2 flex-1 divide-y divide-border">
                        {rows.map(([label, value, id]) => (
                            <li key={id} className="flex items-center justify-between py-1.5 text-sm">
                                <span className="text-foreground/80">{label}</span>
                                <Money value={value} testId={id} className="font-semibold" />
                            </li>
                        ))}
                        <li className="flex items-center justify-between py-2 text-base">
                            <span className="font-semibold">Cash in hand</span>
                            <Money value={r.cash} testId="result-cash-in-hand" className="font-display text-xl font-bold text-primary" />
                        </li>
                    </ul>
                    <button onClick={() => setView("form")} data-testid="estimator-back-btn" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-primary py-2.5 text-base font-semibold text-primary transition-colors hover:bg-primary hover:text-white">
                        <ArrowLeft size={16} /> Back to calculator
                    </button>
                </div>
            )}
        </div>
    );
}
